import { StoreListItem } from '@/class/StoreListItem';

export type SystemAdminStoreForm = {
  name: string;
  furigana: string;
  grade: string;
  zip: string;
  pref: string;
  city: string;
  address: string;
  building: string;
  latitude: string;
  longitude: string;
  billing_zip: string;
  billing_pref: string;
  billing_city: string;
  billing_address: string;
  billing_building: string;
  billing_email: string;
  billing_contact_name: string;
  phone_number: string;
  email: string;
  login_id: string;
  password: string;
  open_time: string;
  close_time: string;
  has_parking: boolean;
};

export const STORE_GRADE_OPTIONS = [
  { value: '1', label: 'すりむにライト' },
  { value: '2', label: 'すりむにスタンダード' },
  { value: '3', label: 'すりむにプレミアム' },
] as const;

export const emptySystemAdminStoreForm = (): SystemAdminStoreForm => ({
  name: '',
  furigana: '',
  grade: '1',
  zip: '',
  pref: '',
  city: '',
  address: '',
  building: '',
  latitude: '',
  longitude: '',
  billing_zip: '',
  billing_pref: '',
  billing_city: '',
  billing_address: '',
  billing_building: '',
  billing_email: '',
  billing_contact_name: '',
  phone_number: '',
  email: '',
  login_id: '',
  password: '',
  open_time: '09:00',
  close_time: '21:00',
  has_parking: false,
});

const normalizeTimeForInput = (value?: string | null): string => {
  const match = value?.match(/(\d{1,2}):(\d{2})/);
  if (!match) {
    return '09:00';
  }

  return `${match[1].padStart(2, '0')}:${match[2]}`;
};

export const storeToEditForm = (store: StoreListItem): SystemAdminStoreForm => ({
  name: store.name ?? '',
  furigana: store.furigana ?? '',
  grade: store.grade != null ? String(store.grade) : '1',
  zip: store.zip ?? '',
  pref: store.pref ?? '',
  city: store.city ?? '',
  address: store.street_address ?? '',
  building: store.building ?? '',
  latitude: store.latitude != null ? String(store.latitude) : '',
  longitude: store.longitude != null ? String(store.longitude) : '',
  billing_zip: store.billing_zip ?? '',
  billing_pref: store.billing_pref ?? '',
  billing_city: store.billing_city ?? '',
  billing_address: store.billing_address ?? '',
  billing_building: store.billing_building ?? '',
  billing_email: store.billing_email ?? '',
  billing_contact_name: store.billing_contact_name ?? '',
  phone_number: store.phone_number ?? '',
  email: store.email ?? '',
  login_id: store.login_id ?? '',
  password: '',
  open_time: normalizeTimeForInput(store.open_time),
  close_time: normalizeTimeForInput(store.close_time),
  has_parking: Boolean(store.has_parking),
});

type SystemAdminStorePayload = {
  name: string;
  furigana: string | null;
  description: string | null;
  zip: string;
  pref: string;
  city: string;
  address: string;
  building: string | null;
  latitude: number;
  longitude: number;
  billing_zip: string | null;
  billing_pref: string | null;
  billing_city: string | null;
  billing_address: string | null;
  billing_building: string | null;
  billing_email: string | null;
  billing_contact_name: string | null;
  phone_number: string;
  email: string;
  login_id: string;
  open_time: string;
  close_time: string;
  has_parking: boolean;
  weather_code: string | null;
  grade?: number;
  password?: string;
};

const parseSystemAdminStoreBaseForm = (
  form: SystemAdminStoreForm,
  options: { requirePassword: boolean; requireGrade: boolean }
): SystemAdminStorePayload | null => {
  if (!form.name.trim()) {
    alert('店舗名を入力してください。');
    return null;
  }
  if (!form.furigana.trim()) {
    alert('ふりがなを入力してください。');
    return null;
  }
  if (!form.zip.trim()) {
    alert('郵便番号を入力してください。');
    return null;
  }
  if (!form.pref.trim()) {
    alert('都道府県を入力してください。');
    return null;
  }
  if (!form.city.trim()) {
    alert('市区町村を入力してください。');
    return null;
  }
  if (!form.address.trim()) {
    alert('住所を入力してください。');
    return null;
  }
  if (!form.latitude.trim() || Number.isNaN(Number(form.latitude))) {
    alert('緯度を入力してください。');
    return null;
  }
  if (!form.longitude.trim() || Number.isNaN(Number(form.longitude))) {
    alert('経度を入力してください。');
    return null;
  }
  if (!form.billing_zip.trim()) {
    alert('請求先郵便番号を入力してください。');
    return null;
  }
  if (!form.billing_pref.trim()) {
    alert('請求先都道府県を入力してください。');
    return null;
  }
  if (!form.billing_city.trim()) {
    alert('請求先市区町村を入力してください。');
    return null;
  }
  if (!form.billing_address.trim()) {
    alert('請求先住所を入力してください。');
    return null;
  }
  if (!form.billing_email.trim()) {
    alert('請求先メールアドレスを入力してください。');
    return null;
  }
  if (!form.billing_contact_name.trim()) {
    alert('請求先担当者を入力してください。');
    return null;
  }
  if (!form.phone_number.trim()) {
    alert('電話番号を入力してください。');
    return null;
  }
  if (!form.email.trim()) {
    alert('メールアドレスを入力してください。');
    return null;
  }
  if (!form.login_id.trim()) {
    alert('ログインIDを入力してください。');
    return null;
  }
  if (options.requirePassword && !form.password.trim()) {
    alert('パスワードを入力してください。');
    return null;
  }
  if (!form.open_time) {
    alert('開店時間を入力してください。');
    return null;
  }
  if (!form.close_time) {
    alert('閉店時間を入力してください。');
    return null;
  }

  const grade = form.grade.trim() === '' ? null : Number(form.grade);
  if (form.grade.trim() !== '' && !STORE_GRADE_OPTIONS.some((option) => option.value === form.grade)) {
    alert('登録プランを選択してください。');
    return null;
  }
  if (options.requireGrade && grade === null) {
    alert('登録プランを選択してください。');
    return null;
  }

  const payload: SystemAdminStorePayload = {
    name: form.name.trim(),
    furigana: form.furigana.trim(),
    description: null,
    zip: form.zip.trim(),
    pref: form.pref.trim(),
    city: form.city.trim(),
    address: form.address.trim(),
    building: form.building.trim() || null,
    latitude: Number(form.latitude),
    longitude: Number(form.longitude),
    billing_zip: form.billing_zip.trim() || null,
    billing_pref: form.billing_pref.trim() || null,
    billing_city: form.billing_city.trim() || null,
    billing_address: form.billing_address.trim() || null,
    billing_building: form.billing_building.trim() || null,
    billing_email: form.billing_email.trim() || null,
    billing_contact_name: form.billing_contact_name.trim() || null,
    phone_number: form.phone_number.trim(),
    email: form.email.trim(),
    login_id: form.login_id.trim(),
    open_time: form.open_time,
    close_time: form.close_time,
    has_parking: form.has_parking,
    weather_code: null,
  };

  if (options.requireGrade) {
    payload.grade = grade as number;
  } else if (grade !== null) {
    payload.grade = grade;
  }

  if (form.password.trim()) {
    payload.password = form.password;
  } else if (options.requirePassword) {
    return null;
  }

  return payload;
};

export const parseSystemAdminStoreForm = (
  form: SystemAdminStoreForm
): (SystemAdminStorePayload & { password: string }) | null => {
  const payload = parseSystemAdminStoreBaseForm(form, { requirePassword: true, requireGrade: true });
  if (!payload?.password) {
    return null;
  }

  return payload as SystemAdminStorePayload & { password: string };
};

export const parseSystemAdminStoreEditForm = (
  form: SystemAdminStoreForm
): SystemAdminStorePayload | null => {
  return parseSystemAdminStoreBaseForm(form, { requirePassword: false, requireGrade: true });
};
